/**
 * Router —— admin 路由 + 全局守卫
 *
 * 守卫规则：
 *   - 未登录访问受保护路由 → 跳 /login + ?redirect=<原路径>
 *   - 已登录访问 /login → 跳 /dashboard
 *   - meta.public === true 的路由免守卫
 *
 * 业务路由按"DefaultLayout 包装 + 子路由"模式组织（Day 18 起接业务页）。
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: { public: true, title: '登录' },
  },
  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      { path: '', redirect: { name: 'dashboard' } },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: { title: '仪表盘' },
      },
      {
        path: 'buildings',
        name: 'buildings',
        component: () => import('@/views/buildings/BuildingsList.vue'),
        meta: { title: '楼栋管理' },
      },
      {
        path: 'buildings/:buildingId/floor-maps',
        name: 'building-floor-maps',
        component: () => import('@/views/buildings/BuildingFloorMapsPage.vue'),
        meta: { title: '楼层地图' },
      },
      {
        path: 'equipment',
        name: 'equipment',
        component: () => import('@/views/equipment/EquipmentList.vue'),
        meta: { title: '设备台账' },
      },
      {
        path: 'equipment/:id',
        name: 'equipment-detail',
        component: () => import('@/views/equipment/EquipmentDetail.vue'),
        meta: { title: '设备详情' },
      },
      {
        path: 'inspection-routes',
        name: 'inspection-routes',
        component: () => import('@/views/inspection/InspectionRouteList.vue'),
        meta: { title: '点检路线' },
      },
      {
        path: 'inspection-tasks',
        name: 'inspection-tasks',
        component: () => import('@/views/inspection/InspectionTaskList.vue'),
        meta: { title: '点检任务' },
      },
      {
        path: 'maintenance-records',
        name: 'maintenance-records',
        component: () => import('@/views/maintenance/MaintenanceRecordList.vue'),
        meta: { title: '维护记录' },
      },
      {
        path: 'patrol-points',
        name: 'patrol-points',
        component: () => import('@/views/patrol/PatrolPointList.vue'),
        meta: { title: '巡更点位' },
      },
      {
        path: 'patrol-routes',
        name: 'patrol-routes',
        component: () => import('@/views/patrol/PatrolRouteList.vue'),
        meta: { title: '巡更路线' },
      },
      {
        path: 'patrol-shift-templates',
        name: 'patrol-shift-templates',
        component: () => import('@/views/patrol/PatrolShiftTemplateList.vue'),
        meta: { title: '巡更班次' },
      },
      {
        path: 'patrol-tasks',
        name: 'patrol-tasks',
        component: () => import('@/views/patrol/PatrolTaskList.vue'),
        meta: { title: '巡更任务' },
      },
      {
        path: 'cleaning/areas',
        name: 'cleaning-areas',
        component: () => import('@/views/cleaning/CleaningAreasList.vue'),
        meta: { title: '保洁区域' },
      },
      {
        path: 'cleaning/routes',
        name: 'cleaning-routes',
        component: () => import('@/views/cleaning/CleaningRoutesList.vue'),
        meta: { title: '保洁路线' },
      },
      {
        path: 'cleaning/shifts',
        name: 'cleaning-shifts',
        component: () => import('@/views/cleaning/CleaningShiftTemplatesList.vue'),
        meta: { title: '保洁班次' },
      },
      {
        path: 'cleaning/tasks',
        name: 'cleaning-tasks',
        component: () => import('@/views/cleaning/CleaningTasksList.vue'),
        meta: { title: '保洁任务' },
      },
      {
        path: 'shops',
        name: 'shops',
        component: () => import('@/views/shops/ShopsList.vue'),
        meta: { title: '商铺管理' },
      },
      {
        path: 'contracts',
        name: 'contracts',
        component: () => import('@/views/contracts/ContractsList.vue'),
        meta: { title: '租赁合同' },
      },
      {
        path: 'merchants',
        name: 'merchants',
        component: () => import('@/views/merchants/MerchantsList.vue'),
        meta: { title: '商户管理' },
      },
      {
        path: 'billing-groups',
        name: 'billing-groups',
        component: () => import('@/views/billing-groups/BillingGroupList.vue'),
        meta: { title: '计费组管理' },
      },
      {
        path: 'billing-groups/:id',
        name: 'billing-group-detail',
        component: () => import('@/views/billing-groups/BillingGroupDetail.vue'),
        meta: { title: '计费组详情' },
      },
      {
        path: 'owners',
        name: 'owners',
        component: () => import('@/views/owners/OwnersList.vue'),
        meta: { title: '业主管理' },
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/views/users/UsersList.vue'),
        meta: { title: '后台账号' },
      },
      {
        path: 'consumers',
        name: 'consumers',
        component: () => import('@/views/consumers/ConsumersList.vue'),
        meta: { title: 'C 端用户' },
      },
      {
        path: 'consumers/:userId',
        name: 'consumer-detail',
        component: () => import('@/views/consumers/ConsumerDetail.vue'),
        meta: { title: 'C 端用户详情', hidden: true },
      },
      {
        path: 'departments',
        name: 'departments',
        component: () => import('@/views/departments/DepartmentsList.vue'),
        meta: { title: '部门管理' },
      },
      {
        path: 'roles',
        name: 'roles',
        component: () => import('@/views/roles/RolesList.vue'),
        meta: { title: '角色与权限' },
      },
      {
        path: 'employees',
        name: 'employees',
        component: () => import('@/views/employees/EmployeesList.vue'),
        meta: { title: '员工管理' },
      },
      {
        path: 'property-fee',
        name: 'property-fee',
        component: () => import('@/views/property-fee/PropertyFeePage.vue'),
        meta: { title: '物业费' },
      },
      {
        path: 'utility',
        name: 'utility',
        component: () => import('@/views/utility/UtilityPage.vue'),
        meta: { title: '水电气' },
      },
      {
        path: 'deposits',
        name: 'deposits',
        component: () => import('@/views/deposits/DepositsList.vue'),
        meta: { title: '押金' },
      },
      {
        path: 'work-orders',
        name: 'work-orders',
        component: () => import('@/views/work-orders/WorkOrdersList.vue'),
        meta: { title: '工单' },
      },
      {
        path: 'complaints',
        name: 'complaints',
        component: () => import('@/views/complaints/ComplaintsList.vue'),
        meta: { title: '投诉建议' },
      },
      {
        path: 'onboarding',
        name: 'onboarding',
        component: () => import('@/views/onboarding/OnboardingWizard.vue'),
        meta: { title: '商户入驻' },
      },
      {
        path: 'offboarding',
        name: 'offboarding',
        component: () => import('@/views/offboarding/OffboardingWizard.vue'),
        meta: { title: '商户撤场' },
      },
      {
        path: 'parking',
        name: 'parking',
        component: () => import('@/views/parking/ParkingList.vue'),
        meta: { title: '月租车牌' },
      },
      {
        path: 'parking/keytop-sync-queue',
        name: 'keytop-sync-queue',
        component: () => import('@/views/parking/KeytopSyncQueueList.vue'),
        meta: { title: 'KEYTOP 同步队列' },
      },
      {
        path: 'advertising',
        name: 'advertising',
        component: () => import('@/views/advertising/AdvertisingPage.vue'),
        meta: { title: '广告' },
      },
      {
        path: 'venue',
        name: 'venue',
        component: () => import('@/views/venue/VenueList.vue'),
        meta: { title: '场地租赁' },
      },
      {
        path: 'sms-templates',
        name: 'sms-templates',
        component: () => import('@/views/sms-templates/SmsTemplatesList.vue'),
        meta: { title: '短信模板' },
      },
      {
        path: 'message-broadcast',
        name: 'message-broadcast',
        component: () => import('@/views/message-broadcast/MessageBroadcastPage.vue'),
        meta: { title: '消息群发' },
      },
      {
        path: 'god-coupon',
        name: 'god-coupon',
        component: () => import('@/views/god-coupon/GodCouponConfig.vue'),
        meta: { title: '天降神券' },
      },
      {
        path: 'coupons',
        name: 'coupons',
        component: () => import('@/views/coupons/CouponsList.vue'),
        meta: { title: '优惠券' },
      },
      {
        path: 'members',
        name: 'admin-members',
        component: () => import('@/views/members/MemberList.vue'),
        meta: { title: '会员管理' },
      },
      {
        path: 'members/levels',
        name: 'member-levels',
        component: () => import('@/views/members/MemberLevelList.vue'),
        meta: { title: '会员等级' },
      },
      {
        path: 'home/banners',
        name: 'home-banners',
        component: () => import('@/views/home/BannerList.vue'),
        meta: { title: '首页 Banner' },
      },
      {
        path: 'activities',
        name: 'activities',
        component: () => import('@/views/activities/ActivitiesList.vue'),
        meta: { title: '商场活动' },
      },
      {
        path: 'merchant-campaigns',
        name: 'merchant-campaigns',
        component: () => import('@/views/merchant-campaigns/MerchantCampaignsPage.vue'),
        meta: { title: '商户活动' },
      },
      {
        path: 'merchants/featured',
        name: 'merchants-featured',
        component: () => import('@/views/merchants/FeaturedList.vue'),
        meta: { title: '优质商户挂牌' },
      },
      {
        path: 'notifications',
        name: 'admin-notifications',
        component: () => import('@/views/notifications/NotificationList.vue'),
        meta: { title: '通知管理' },
      },
      {
        path: 'finance/reports',
        name: 'finance-reports',
        component: () => import('@/views/finance/FinanceReportPage.vue'),
        meta: { title: '财务报表' },
      },
      {
        path: 'system/platform-profile',
        name: 'platform-profile',
        component: () => import('@/views/system/PlatformProfilePanel.vue'),
        meta: { title: '平台配置' },
      },
      {
        path: 'system/sms-logs',
        name: 'sms-logs',
        component: () => import('@/views/system/SmsLogsPanel.vue'),
        meta: { title: '短信发送日志' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFound.vue'),
    meta: { public: true, title: '页面未找到' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach((to) => {
  const auth = useAuthStore()
  const isPublic = to.meta.public === true

  if (!isPublic && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (auth.isAuthenticated && to.name === 'login') {
    return { name: 'dashboard' }
  }
  return true
})

router.afterEach((to) => {
  const baseTitle = '银都 OS · 管理后台'
  const pageTitle = (to.meta.title as string | undefined) ?? ''
  document.title = pageTitle === '' ? baseTitle : `${pageTitle} - ${baseTitle}`
})
